import React, { Fragment, useCallback } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import {
  Card,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  withTheme,
} from '@material-ui/core';
import { Clear, Visibility, VisibilityOff } from '@material-ui/icons';

import { AscendState } from '../../ducks';
import { HIDE_ACCOUNT, REMOVE_MEMBER, SHOW_ACCOUNT } from '../../ducks/actions';
import { MxAccount, MxMember } from '../../models/mx';
import { formatFieldValue } from '../shared/format';

type StateProps = {
  accounts: MxAccount[];
  members: MxMember[];
  userId: number;
};
type DispatchProps = {
  hideAccount: (userId: number, accountGuid?: string) => void;
  removeMember: (userId: number, memberGuid?: string) => void;
  showAccount: (userId: number, accountGuid?: string) => void;
};
type AccountsTableProps = StateProps & DispatchProps;

// todo I'd like to automatically redirect to accounts page if user has no accounts,
//  maybe in a useEffect that waits for accounts to load

const AccountsTable = ({
  accounts,
  hideAccount,
  members,
  removeMember,
  showAccount,
  userId,
}: AccountsTableProps) => {
  const hideUserAccount = useCallback(
    (accountGuid?: string) => hideAccount(userId, accountGuid),
    [userId]
  );
  const removeUserMember = useCallback(
    (memberGuid?: string) => removeMember(userId, memberGuid),
    [userId]
  );
  const showUserAccount = useCallback(
    (accountGuid?: string) => showAccount(userId, accountGuid),
    [userId]
  );

  return (
    <Card elevation={2}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell style={{ paddingLeft: 24, paddingRight: 24 }}>Account Name</TableCell>
            <TableCell style={{ paddingLeft: 24, paddingRight: 24, textAlign: 'right' }}>
              Balance
            </TableCell>
            <TableCell style={{ paddingLeft: 12, paddingRight: 12 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {/* todo I imagine this loop isn't great, but fine for v1? */}
          {members.map((member: MxMember) => (
            <Fragment key={member.guid}>
              <TableRow>
                <TableCell style={{ fontWeight: 600, paddingLeft: 24, paddingRight: 24 }}>
                  {member.name}
                </TableCell>
                <TableCell style={{ paddingLeft: 24, paddingRight: 24 }}></TableCell>
                <TableCell style={{ paddingLeft: 12, paddingRight: 12, textAlign: 'right' }}>
                  <IconButton
                    style={{ backgroundColor: '#ff2725', padding: 2 }}
                    onClick={() => removeUserMember(member.guid)}>
                    <Clear style={{ color: '#ffffff' }} />
                  </IconButton>
                </TableCell>
              </TableRow>
              {accounts
                .filter((account: MxAccount) => account.member_guid === member.guid)
                .map((account: MxAccount) => (
                  <TableRow key={account.guid}>
                    {/* default padding is 24 */}
                    <TableCell
                      scope="row"
                      style={{
                        color: account.is_closed ? '#909090' : 'rgba(0, 0, 0, 0.87)',
                        paddingLeft: 24,
                        paddingRight: 24,
                      }}>
                      {account.name}
                    </TableCell>
                    <TableCell
                      style={{
                        color: account.is_closed ? '#909090' : 'rgba(0, 0, 0, 0.87)',
                        paddingLeft: 24,
                        paddingRight: 24,
                        textAlign: 'right',
                      }}>
                      {formatFieldValue(account.balance, 'number', 'dollar')}
                    </TableCell>
                    <TableCell style={{ paddingLeft: 12, paddingRight: 12, textAlign: 'right' }}>
                      <IconButton
                        style={{ backgroundColor: '#808080', padding: 2 }}
                        title={account.is_closed ? 'Show' : 'Hide'}
                        onClick={() =>
                          account.is_closed
                            ? showUserAccount(account.guid)
                            : hideUserAccount(account.guid)
                        }>
                        {account.is_closed ? (
                          <Visibility style={{ color: '#ffffff' }} />
                        ) : (
                          <VisibilityOff style={{ color: '#ffffff' }} />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </Fragment>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
};

const mapStateToProps = (state: AscendState) => ({
  accounts: state.mx.accounts.data,
  connectUrl: state.mx.connectUrl.data,
  members: state.mx.members.data,
  userId: state.user.data.id,
});

// noinspection JSUnusedGlobalSymbols
const mapDispatchToProps = (dispatch: Dispatch) => ({
  hideAccount: (userId: number, accountGuid?: string) =>
    dispatch({ type: HIDE_ACCOUNT, userId, accountGuid }),
  removeMember: (userId: number, memberGuid?: string) =>
    dispatch({ type: REMOVE_MEMBER, userId, memberGuid }),
  showAccount: (userId: number, accountGuid?: string) =>
    dispatch({ type: SHOW_ACCOUNT, userId, accountGuid }),
});

export default withTheme()(
  connect<StateProps, DispatchProps>(mapStateToProps, mapDispatchToProps)(AccountsTable)
);
